/**
 * Proposal Details for the Nexus application.
 *
 * <i>Copyright (c) 2020 Energi Cryptocurrency - https://energi.world</i><br>
 * Proprietary License
 *
 *
 * @module components/subpages/treasury-budget/proposal-details.jsx
 * @class ProposalDetails
 * @since 0.3.8
*/
import React, {useState, useEffect} from 'react';
import Web3 from 'web3';
import {DataTable, DataTableContent, DataTableBody, DataTableRow, DataTableCell} from '@rmwc/data-table';
import {Snackbar, SnackbarAction} from '@rmwc/snackbar';
import {IconButton} from '@rmwc/icon-button';
import {Tooltip} from '@rmwc/tooltip';
import Footer from '../../Footer';
import formatNumber from '../../../lib/helpers/bn-format';
import fetchBlockInformation from '../../../lib/helpers/blocks';
import imgContentCopy24 from '../../../images/content_copy_24.png';
import copyToClipboard from '../../../lib/helpers/clipboard';
import translations from '../../../lib/translations';
import menuState from '../../../globals/menu';

const BN100 = Web3.utils.toBN('100');

const deadlineDate = (date, currentLang) => new Intl.DateTimeFormat(currentLang, {
    weekday: 'long',
    month: 'long',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
    timeZone: 'UTC',
}).format(date);

const ProposalDetails = props => {
    const data = props.data,
        // lastBlock = useStore(blockPolling.$lastBlock),
        [snackbarMsg, setSnackbarMsg] = useState(''),
        [blockTime, setBlockTime] = useState(),
        locales = props.langInfo && props.langInfo.locales,
        translate = translations.init(props);
    let notificationWrapper, status, classnameStatus, votes;

    useEffect(() => {
        menuState.on.hide();
        window.scrollTo(0, 0);
        fetchBlock(data.createdBlock);

        return () => {
            menuState.on.open();
        };
    }, []);

    const fetchBlock = async (blockNumber) => {
        const block = await fetchBlockInformation(blockNumber);
        const date = new Date(block.timestamp * 1000);
        const formattedDate = deadlineDate(date, props.langInfo.lang);

        setBlockTime(formattedDate);
    };

    const copyClipboard = (text, what) => {
        if (copyToClipboard(text)) {
            // do not translate <what< -> these are Strings like public address
            setSnackbarMsg(what + ' ' + translate.t('COPIED_CLIPBOARD'));
        }
        else {
            setSnackbarMsg(translate.t('ACTION_NOT_SUPPORTED', true));
        }
    };

    votes = data.acceptWeight.add(data.rejectWeight);
    status = votes.mul(BN100).div(data.quorumWeight).toString();

    if (votes.lt(data.quorumWeight)) {
        classnameStatus = 'notenough-votes';
    }
    else if (data.acceptWeight.gt(data.rejectWeight)) {
        classnameStatus = 'positive-votes';
    }
    else  {
        classnameStatus = 'negative-votes';
    }

    return (
        <div className="treasury-budget pure-u subpage no-menu">
            <section className="details">
                <div className="top-bar">
                    <table>
                        <tbody>
                            <tr>
                                <td><IconButton
                                    icon="arrow_back"
                                    onClick={() => props.setState(0)}
                                    style={{color: 'rgba(0, 0, 0, 0.54)'}}/>
                                </td>
                                <td><span className="top-bar-text">{translate.t('PROPOSAL_DETAILS', true)}</span></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                {notificationWrapper}
                <div className="data-table">
                    <DataTable className="data-table-wrapper table-shadow proposal-details">
                        <DataTableContent className="data-table-wrapper">
                            <DataTableBody>
                                <DataTableRow>
                                    <DataTableCell>
                                        <Tooltip content={translate.t('PROPOSAL_UUID_EXPLANATION', true)}>
                                            <span>{translate.t('UUID', true)}</span>
                                        </Tooltip>
                                    </DataTableCell>
                                    <DataTableCell alignStart>
                                        <table>
                                            <tbody>
                                                <tr>
                                                    <td>{data.refUUID}</td>
                                                    <td>
                                                        <Tooltip content={translate.t('COPY_UUID', true)}>
                                                            <IconButton
                                                                className="img-fix"
                                                                icon={imgContentCopy24}
                                                                onClick={() => copyClipboard(data.refUUID, translate.t('UUID', true))} />
                                                        </Tooltip>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>
                                        <Tooltip content={translate.t('PROPOSAL_REQUESTED_EXPLANATION', true)}>
                                            <span>{translate.t('REQUESTED', true)}</span>
                                        </Tooltip>
                                    </DataTableCell>
                                    <DataTableCell alignStart>{formatNumber(locales, data.proposedAmount, 2)} NRG</DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>
                                        <Tooltip content={translate.t('PROPOSAL_PROPOSER_EXPLANATION', true)}>
                                            <span>{translate.t('PROPOSER', true)}</span>
                                        </Tooltip>
                                    </DataTableCell>
                                    <DataTableCell alignStart>
                                        <table>
                                            <tbody>
                                                <tr>
                                                    <td>{data.proposer}</td>
                                                    <td>
                                                        <Tooltip content={translate.t('COPY_PROPOSER', true)}>
                                                            <IconButton
                                                                className="img-fix"
                                                                icon={imgContentCopy24}
                                                                onClick={() => copyClipboard(data.proposer, translate.t('PROPOSER', true))} />
                                                        </Tooltip>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>
                                        <Tooltip content={translate.t('PROPOSAL_YES_EXPLANATION', true)}>
                                            <span>{translate.t('YES_VOTES', true)}</span>
                                        </Tooltip>
                                    </DataTableCell>
                                    <DataTableCell alignStart className="accepted-votes">{formatNumber(locales, data.acceptWeight, 0)}</DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>
                                        <Tooltip content={translate.t('PROPOSAL_NO_EXPLANATION', true)}>
                                            <span>{translate.t('NO_VOTES', true)}</span>
                                        </Tooltip>
                                    </DataTableCell>
                                    <DataTableCell alignStart className="rejected-votes">{formatNumber(locales, data.rejectWeight, 0)}</DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>
                                        <Tooltip content={translate.t('PROPOSAL_QUORUM_EXPLANATION', true)}>
                                            <span>{translate.t('QUORUM', true)}</span>
                                        </Tooltip>
                                    </DataTableCell>
                                    <DataTableCell alignStart>{formatNumber(locales, data.quorumWeight, 0)}</DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>
                                        <Tooltip content={translate.t('ACTIVE_COLLATERAL_WHEN_PROPOSED_EXPLANATION', true)}>
                                            <span>{translate.t('ACTIVE_COLLATERAL_WHEN_PROPOSED', true)}</span>
                                        </Tooltip>
                                    </DataTableCell>
                                    <DataTableCell alignStart>{formatNumber(locales, data.totalWeight, 0)}</DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>
                                        <Tooltip content={translate.t('PROPOSAL_STATUS_EXPLANATION', true)}>
                                            <span>{translate.t('STATUS', true)}</span>
                                        </Tooltip>
                                    </DataTableCell>
                                    <DataTableCell alignStart className={classnameStatus}>{status + '%'}</DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>
                                        <Tooltip content={translate.t('PROPOSAL_VOTING_DEADLINE_EXPLANATION', true)}>
                                            <span>{translate.t('VOTING_DEADLINE', true)}</span>
                                        </Tooltip>
                                    </DataTableCell>
                                    <DataTableCell alignStart>{deadlineDate(data.deadline, props.langInfo.lang)}</DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>
                                        <Tooltip content={translate.t('TIMESTAMP_EXPLANATION', true)}>
                                            <span>{translate.t('TIMESTAMP', true)}</span>
                                        </Tooltip>
                                    </DataTableCell>
                                    <DataTableCell alignStart>{blockTime ? blockTime : 'Loading..'}</DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>
                                        <Tooltip content={translate.t('BLOCK_NUMBER_EXPLANATION', true)}>
                                            <span>{translate.t('BLOCK_NUMBER', true)}</span>
                                        </Tooltip>
                                    </DataTableCell>
                                    <DataTableCell alignStart>{data.createdBlock}</DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>
                                        <Tooltip content={translate.t('PROPOSAL_ADDRESS_EXPLANATION', true)}>
                                            <span>{translate.t('PROPOSAL_ADDRESS', true)}</span>
                                        </Tooltip>
                                    </DataTableCell>
                                    <DataTableCell alignStart>
                                        <table>
                                            <tbody>
                                                <tr>
                                                    <td>{data.proposal}</td>
                                                    <td>
                                                        <Tooltip content={translate.t('COPY_PROPOSAL', true)}>
                                                            <IconButton
                                                                className="img-fix"
                                                                icon={imgContentCopy24}
                                                                onClick={() => copyClipboard(data.proposal, translate.t('PROPOSAL_ADDRESS', true))} />
                                                        </Tooltip>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </DataTableCell>
                                </DataTableRow>
                            </DataTableBody>
                        </DataTableContent>
                    </DataTable>
                </div>
            </section>
            <Snackbar
                action={<SnackbarAction icon="close" />}
                leading
                message={snackbarMsg}
                onClose={() => setSnackbarMsg('')}
                open={!!snackbarMsg}
                timeout={2020}/>
            <Footer {...props} />
        </div>
    );
};

export default ProposalDetails;
